import { every, intersection, isArray, isEqual, isObject, isPlainObject, omitBy, transform, union, without } from "lodash-es"

type AllowedItem = Deltable | Array<AllowedItem> | string | boolean | number | null | undefined
type Deltable = {
    [key: string]: AllowedItem
}

const Delta = {
    NO_CHANGE: Symbol(),
    REMOVED: Symbol(),
} as const
Object.seal(Delta)

export function deltaWasRemoved(test: any) {
    return test === Delta.REMOVED
}

export function deltaNoChange(test: any) {
    return test === Delta.NO_CHANGE
}

export function delta<T extends Deltable>(
    newObject: Partial<T>,
    original: Partial<T>
): Partial<Deltable> {
    const rtn = deltaUnder(<AllowedItem>newObject, <AllowedItem>original)
    if (deltaNoChange(rtn)) {
        return {}
    }
    if (deltaWasRemoved(rtn)) {
        throw new Error("how can something that never existed be removed?")
    }
    return <Partial<T>>rtn
}

function deltaUnder(
    newObject: AllowedItem,
    original: AllowedItem
): AllowedItem | symbol {
    // If they're different types it's guaranteed they are different
    if (typeof newObject !== typeof original) {
        return newObject
    }

    if (typeof newObject === "boolean") {
        return newObject === original ? Delta.NO_CHANGE : newObject
    }

    if (typeof newObject === "string") {
        return newObject === original ? Delta.NO_CHANGE : newObject
    }

    if (typeof newObject === "number") {
        return newObject === original ? Delta.NO_CHANGE : newObject
    }

    if (typeof newObject === "object") {

        // Check array equivalency
        if (isArray(newObject) !== isArray(original)) {
            return newObject
        }

        if (isArray(newObject) && isArray(original)) {
            return isEqual(newObject, original) ? Delta.NO_CHANGE : newObject
        }

        if (newObject === null && original === null) {
            return Delta.NO_CHANGE
        }

        if (newObject === null || original === null) {
            return newObject
        }

        if (newObject === undefined && original === undefined) {
            return Delta.NO_CHANGE
        }

        if (newObject === undefined || original === undefined) {
            return newObject
        }

        // They're either both arrays or both objects
        if (isPlainObject(newObject) && isPlainObject(original)) {
            const returnObj: Record<
                string,
                AllowedItem | typeof Delta.NO_CHANGE | typeof Delta.REMOVED
            > = {}

            const newEntries = Object.keys(newObject)
            const oldEntries = Object.keys(original) // Just some logic typescript missed, if newObj and original are the same, original can't be undefined or null

            // break into groups
            const newKeys = without(newEntries, ...oldEntries)
            const deletedKeys = without(oldEntries, ...newEntries)
            const restKeys = intersection(newEntries, oldEntries)

            newKeys.forEach((key) => {
                returnObj[key] = (<Deltable>newObject)[key]
            })

            deletedKeys.forEach((key) => {
                returnObj[key] = Delta.REMOVED
            })

            restKeys.forEach((key) => {
                const originalElement = (<Deltable>original)[key]
                const newElement = (<Deltable>newObject)[key]
                const res = deltaUnder(newElement, originalElement)
                returnObj[key] = res
            })

            if (every(returnObj, v => v === Delta.NO_CHANGE)) return Delta.NO_CHANGE
            return <AllowedItem>omitBy(returnObj, val => val === Delta.NO_CHANGE)
        }

        return newObject === original ? Delta.NO_CHANGE : newObject

        // console.error({ newObject, original })
        // throw new Error("New object and original were not both arrays or both plain objects")
    }

    console.error({ newObject, original })
    throw new Error("New object and/or original did not fit allowed data types")
}
